import type { Vec2 } from './vec';
import { add, dist, normalize, perp, scale, sub } from './vec';
import { angleAt } from './angle';
import { circleCircleIntersect } from './intersect';

const eps = 1e-9;

/**
 * Compute the inscribed angle ∠APB, where A and B are chord endpoints
 * and P is the vertex on the circle.
 *
 * Returns the unsigned angle in degrees.
 */
export function inscribedAngle(A: Vec2, B: Vec2, P: Vec2): number {
  return angleAt(P, A, B);
}

/**
 * Compute the central angle ∠AOB, where A and B are points on the circle
 * and O is the center.
 *
 * Returns the unsigned angle in degrees.
 */
export function centralAngle(A: Vec2, B: Vec2, O: Vec2): number {
  return angleAt(O, A, B);
}

/**
 * Compute the unit direction vector perpendicular to the radius OT.
 *
 * This is the direction of the tangent line to the circle at point T.
 * Returns a unit vector perpendicular to OT.
 */
export function tangentDirection(O: Vec2, T: Vec2): Vec2 {
  const radius = sub(T, O);
  const perpendicular = perp(radius);
  return normalize(perpendicular);
}

/**
 * Compute the tangent point(s) from an external point P to a circle centered at O with radius r.
 *
 * Returns:
 * - Empty array if P is strictly inside the circle (dist(O, P) < r - eps)
 * - One point if P is on the circle (|dist(O, P) - r| ≤ eps)
 * - Two points if P is strictly outside the circle
 *
 * The points are ordered by (x, y) ascending (from circleCircleIntersect).
 */
export function tangentPointsFromExternal(O: Vec2, r: number, P: Vec2): Vec2[] {
  const d = dist(O, P);

  // P is strictly inside
  if (d < r - eps) {
    return [];
  }

  // P is on the circle
  if (Math.abs(d - r) <= eps) {
    return [{ x: P.x, y: P.y }];
  }

  // P is strictly outside: construct Thales circle on diameter OP
  const thalesCenter = scale(add(O, P), 0.5);
  const thalesRadius = d / 2;

  // Intersect the Thales circle with the given circle
  const intersections = circleCircleIntersect(thalesCenter, thalesRadius, O, r);

  return intersections;
}

/**
 * Compute the power of a point P with respect to a circle centered at O with radius r.
 *
 * Power = dist(P, O)² - r²
 *
 * Returns:
 * - Negative if P is inside the circle
 * - Zero if P is on the circle
 * - Positive if P is outside the circle
 */
export function powerOfPoint(P: Vec2, O: Vec2, r: number): number {
  const d = dist(P, O);
  return d * d - r * r;
}

/**
 * Compute the sum of opposite angle pairs in a cyclic quadrilateral.
 *
 * Given 4 points in order, computes the 4 interior angles using angleAt.
 * Returns [angle0 + angle2, angle1 + angle3] where angles 0,1,2,3 are
 * at vertices quad[0], quad[1], quad[2], quad[3] respectively.
 *
 * For a cyclic quadrilateral, both sums should be 180 degrees.
 */
export function cyclicOppositeSum(quad: readonly Vec2[]): [number, number] {
  const angle0 = angleAt(quad[0]!, quad[3]!, quad[1]!);
  const angle1 = angleAt(quad[1]!, quad[0]!, quad[2]!);
  const angle2 = angleAt(quad[2]!, quad[1]!, quad[3]!);
  const angle3 = angleAt(quad[3]!, quad[2]!, quad[0]!);

  return [angle0 + angle2, angle1 + angle3];
}

/**
 * Compute the Thales circle (the circle with diameter AB).
 *
 * Returns the center and radius of the circle.
 * Center = midpoint(A, B)
 * Radius = dist(A, B) / 2
 */
export function thalesCircle(A: Vec2, B: Vec2): { center: Vec2; r: number } {
  const center = scale(add(A, B), 0.5);
  const r = dist(A, B) / 2;
  return { center, r };
}
